import React, { useEffect, useState, useContext } from "react";
import { Button, ConfirmationDialog, ProtectedComponent, RenderName, StatusCount, TextInput } from "../../components";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../redux/store";
import { Link, useNavigate, useParams } from "react-router-dom";
import { getOffice, sortObj } from "../../redux/slices/OfficeSlice";
import { TableHeader, TableRow } from "../../components/TableComponents/TableComponents";
import OfficeSectionsHeader from "../../components/OfficeSectionsHeader/OfficeSectionsHeader";
import { clearRequestData, getAllPCP, getOfficeRequestCounts, searchOfficeRequests } from "../../redux/slices/OfficeRequestSlice";
import { getOfficeUsersWithPermission } from "../../redux/slices/OfficeSlice";
import { RefundRequestState, Request, RequestState } from "../../models/request.model";
import { Pagination } from "../../components";
import { AuthPermission } from "../../services/auth.service";
import { User } from "../../models/user.model";
import moment from "moment";
import { ToastContext } from "../../contexts/ToastContext/ToastContext";
import { setActiveIndex } from "../../redux/slices/RequestSlice";
import { billedToOptions, getSorting, renderState } from "../../utils";
import { headers } from "./OfficeDetailData";
import { CircularProgress, Tooltip as MuiTooltip } from "@mui/material";
import NoRequestFound from "../../assets/images/no-result.png";
import DataNotFound from "../../components/DataNotFound/DataNotFound";
import { ArchiveState } from "../../models/request.model";
import { SESSION_STORAGE_ARCHIVE_STATE_TYPE, SESSION_STORAGE_TEMP_CLOSED, colors } from "../../utils/constants";
import { useDebouncedCallback } from "use-debounce";
import { searchServiceData } from "../../redux/slices/ServiceSlice";
import { ServiceOwner } from "../../models/service.model";
import { OfficeState } from "../../models/office.model";
import { OfficeService } from "../../services/office.service";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";

const statusCountsInit = [
  { id: "urgent", title: "Urgent", count: 0, isDanger: true, type: "urgent" },
  { id: "pendingVerification", title: "Pending Verification", count: 0, isDanger: false, type: RequestState.PendingVerification },
  { id: "pendingPayment", title: "Pending Payment", count: 0, isDanger: false, type: RequestState.PendingPayment },
  { id: "inProgress", title: "In Progress", count: 0, isDanger: false, type: RequestState.InProgress },
  { id: "completed", title: "Complete", count: 0, isDanger: false, type: RequestState.Complete },
  // { id: "cancelled", title: "Cancel Request", count: 0, isDanger: false, type: RequestState.Cancelled },
];

const usersList = [
  {
    patientName: "Jane Doe",
    assignTo: "Jillian Ross",
    serviceName: "Disability Tax Credit",
    pcp: "Dr. William Mahoney",
    status: "Under Review",
    createdAt: "16/07/2022",
    commentsCount: 3,
    newCommentsCount: 1,
  },
];
interface Pcp {
  userId: string;
  firstName: string;
  lastName: string;
  email: string;
  permissions: any;
  acceptedTerms: boolean;
  annualPlanMember: boolean;
  // add other fields here
}

const OfficeDetailsScreen: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const toast = useContext(ToastContext);
  const [tooltipStates, setTooltipStates] = useState<boolean[]>(new Array(usersList ? usersList.length : 10).fill(false));
  const [sortMethods, setSortMethods] = useState<boolean[]>(headers.map(() => false));
  const [activeSortIndex, setActiveSortIndex] = useState<number>(2);
  const [sortKeys] = useState(headers.map(head => head.sortKey));
  const [statusCounts, setStatusCounts] = useState<typeof statusCountsInit>(statusCountsInit);
  const [tableFilter, setTableFilter] = useState<RequestState | "urgent" | null>(null);
  const [selectedUser, setSelectedUser] = useState("");
  const [selectedPcp, setSelectedPcp] = useState("");
  const [selectedArchiveState, setSelectedArchiveState] = useState<ArchiveState>(ArchiveState.NotArchived);
  const { id } = useParams();
  const [term, setTerm] = useState<string>("");
  const [isLoading, setIsLoading] = useState(false);
  const [showCloseAlert, setShowCloseAlert] = useState(false);
  const { officeRequestList, requestCounts, loading } = useSelector((state: RootState) => state.officeRequest);
  const { userWithPermission, currentOffice, loadingOffice } = useSelector((state: RootState) => state.office);
  const { serviceList } = useSelector((state: RootState) => state.service);
  const [pcpData, setPcpData] = useState<Pcp[]>([]);
  const moreOptions = ["View Details"];
  const [sort, setSort] = useState({
    start: 0,
    end: 10,
    pageNumber: 0,
    rowsPerPage: 10,
    sortedBy: "requestNumber,desc",
  });
  const refreshStatusCount = (val: ArchiveState, assignedTo?: string) => {
    if (id && val && val !== ArchiveState.Archived) {
      dispatch(getOfficeRequestCounts({ officeId: id, assignedTo, archiveState: val }));
    }
  };

  const handleAllUserSelect = (e: string) => {
    setSelectedUser(e);
    refreshStatusCount(selectedArchiveState, e);
    updateTable(
      { ...sort, pageNumber: 0 },
      term,
      tableFilter === "urgent",
      tableFilter && tableFilter !== "urgent" ? tableFilter : undefined,
      e,
      selectedArchiveState,
      selectedPcp
    );
  };

  const handleSelectPCP = (e: string) => {
    setSelectedPcp(e);
    refreshStatusCount(selectedArchiveState, e);
    updateTable(
      { ...sort, pageNumber: 0 },
      term,
      tableFilter === "urgent",
      tableFilter && tableFilter !== "urgent" ? tableFilter : undefined,
      selectedUser, // also send User if selected
      selectedArchiveState,
      e // primaryCareProvider
    );
  };
  const updateTable = (
    sortObj?: sortObj,
    searchText?: string,
    urgent?: boolean,
    filter?: RequestState,
    user?: string,
    archive?: string,
    primaryCareProvider?: string
  ) => {
    const pageNumToBeCalled = sortObj?.pageNumber ?? sort.pageNumber;
    const sizeToBeCalled = sortObj?.rowsPerPage ?? sort.rowsPerPage;
    const sortBy = sortObj?.sortedBy ?? sort.sortedBy;
    const searchT = searchText ?? "";
    const archiveVal = archive ? (archive as ArchiveState) : ArchiveState.NotArchived;
    const tabFilter = archiveVal !== ArchiveState.Archived ? filter ?? null : undefined;
    if (!id) return;
    dispatch(
      searchOfficeRequests({
        officeId: id,
        searchTerm: searchT,
        assignedTo: user !== "" ? user : undefined,
        primaryCareProvider: primaryCareProvider !== "" ? primaryCareProvider : undefined, // ✅ pcp
        page: pageNumToBeCalled,
        size: sizeToBeCalled,
        sort: sortBy,
        state: tabFilter ?? undefined,
        urgent: archiveVal !== ArchiveState.Archived && urgent ? urgent : undefined,
        archiveState: archiveVal,
      })
    ).then(() => {
      setSort({ ...sort, pageNumber: pageNumToBeCalled, rowsPerPage: sizeToBeCalled, sortedBy: sortBy });
    });
  };
  const handleSort = (index: number, firstTime = false) => {
    const sortMethodString = getSorting(sortKeys, index, sortMethods);
    setSortMethods([...sortMethods.slice(0, index), !sortMethods[index], ...sortMethods.slice(index + 1)]);
    let finalArchiveStateVal = selectedArchiveState;
    if (firstTime && id) {
      const archiveStateVal = sessionStorage.getItem(SESSION_STORAGE_ARCHIVE_STATE_TYPE);
      const val: ArchiveState = archiveStateVal ? (archiveStateVal as ArchiveState) : ArchiveState.NotArchived;
      setSelectedArchiveState(val);
      finalArchiveStateVal = val;
      refreshStatusCount(finalArchiveStateVal);
    }
    updateTable(
      { ...sort, pageNumber: 0, sortedBy: sortMethodString },
      term,
      tableFilter === "urgent",
      tableFilter && tableFilter !== "urgent" ? tableFilter : undefined,
      selectedUser,
      finalArchiveStateVal,
      selectedPcp
    );
    setActiveSortIndex(index);
  };

  const onPageChange = (e: React.MouseEvent<HTMLButtonElement> | null, newPage: number) => {
    updateTable(
      { ...sort, pageNumber: newPage },
      term,
      tableFilter === "urgent",
      tableFilter && tableFilter !== "urgent" ? tableFilter : undefined,
      selectedUser,
      selectedArchiveState,
      selectedPcp
    );
  };

  const onRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    updateTable(
      { ...sort, pageNumber: 0, rowsPerPage: parseInt(event.target.value, 10) },
      term,
      tableFilter === "urgent",
      tableFilter && tableFilter !== "urgent" ? tableFilter : undefined,
      selectedUser,
      selectedArchiveState,
      selectedPcp
    );
  };

  const handleTooltipClick = (type: string, id: string) => {
    switch (type) {
      case "View Details":
        navigate(id);
        break;
      default:
        toast?.openToast();
        break;
    }
    setTooltipStates([...tooltipStates].fill(false));
  };
  const handleToolTipOpen = (index: number) => {
    setTooltipStates([...tooltipStates.slice(0, index).fill(false), true, ...tooltipStates.slice(index + 1).fill(false)]);
  };

  const debounced = useDebouncedCallback(value => {
    if (value && value.trim().length >= 2) {
      updateTable(
        { ...sort, pageNumber: 0 },
        value.trim(),
        tableFilter === "urgent",
        tableFilter && tableFilter !== "urgent" ? tableFilter : undefined,
        selectedUser,
        selectedArchiveState,
        selectedPcp
      );
    }
  }, 1000);

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    const input = e.currentTarget.value;
    const oldSearchTerm = term;
    setTerm(input);
    debounced(input);
    if (oldSearchTerm !== "" && input.length <= 1) {
      updateTable(
        sort,
        "",
        tableFilter === "urgent",
        tableFilter && tableFilter !== "urgent" ? tableFilter : undefined,
        selectedUser,
        selectedArchiveState,
        selectedPcp
      );
    }
  };

  const handleToolTipClose = () => {
    setTooltipStates([...tooltipStates].fill(false));
  };

  const onRowClick = (requestId: string) => {
    dispatch(clearRequestData());
    navigate(requestId);
  };

  const setDefaultSorting = () => {
    let sortMethodString = sortKeys[2];
    sortMethodString += ",desc";
    setActiveSortIndex(2);
    setSortMethods([...sortMethods.slice(0, 2), true, ...sortMethods.slice(2 + 1)]);
    return sortMethodString;
  };

  const onStatusCountClick = (statusId: string) => {
    if (id) {
      if (statusId === "urgent") {
        setTableFilter(tableFilter === statusId ? null : "urgent");
        const sorting = setDefaultSorting();
        updateTable(
          { ...sort, sortedBy: sorting },
          term,
          tableFilter === statusId ? undefined : true,
          undefined,
          selectedUser,
          selectedArchiveState,
          selectedPcp
        );
      } else {
        let state;
        switch (statusId) {
          case "pendingVerification":
            state = RequestState.PendingVerification;
            break;
          case "pendingPayment":
            state = RequestState.PendingPayment;
            break;
          case "inProgress":
            state = RequestState.InProgress;
            break;
          case "waitingForSignature":
            state = RequestState.WaitingSignature;
            break;
          case "finalReview":
            state = RequestState.FinalReview;
            break;
          case "completed":
            state = RequestState.Complete;
            break;
          case "cancelled":
            state = RequestState.Cancelled;
            break;
        }
        if (tableFilter === state) {
          const sorting = setDefaultSorting();
          updateTable({ ...sort, pageNumber: 0, sortedBy: sorting }, term, undefined, undefined, selectedUser, selectedArchiveState, selectedPcp);
        } else {
          updateTable(
            { ...sort, pageNumber: 0, sortedBy: "updatedDate,desc" },
            term,
            undefined,
            state,
            selectedUser,
            selectedArchiveState,
            selectedPcp
          );
          setActiveSortIndex(6);
          setSortMethods([...sortMethods.slice(0, 6), true, ...sortMethods.slice(6 + 1)]);
        }
        if (state) setTableFilter(tableFilter === state ? null : state);
      }
    }
  };

  const handleArchiveStateChange = (val: ArchiveState) => {
    setSelectedArchiveState(val);
    if (val === ArchiveState.Archived) {
      setTableFilter(null);
    } else {
      refreshStatusCount(val, selectedUser);
    }
    sessionStorage.setItem(SESSION_STORAGE_ARCHIVE_STATE_TYPE, val);
    updateTable(
      { ...sort, pageNumber: 0 },
      term,
      tableFilter === "urgent",
      tableFilter && tableFilter !== "urgent" ? tableFilter : undefined,
      selectedUser,
      val,
      selectedPcp
    );
  };

  const renderCloseMessage = () => {
    return (
      <div className="flex flex-col gap-4">
        <h2 className="font-bold">The office is currently closed. You will need to open in order to access requests.</h2>
      </div>
    );
  };

  const headerIconAsc = <p> ↑</p>;
  const headerIconDesc = <p> ↓</p>;

  // Create display headers based on cancelled state
  const displayHeaders =
    tableFilter === RequestState.Cancelled
      ? [
          ...headers.filter(header => header.title !== "Comments"),
          {
            title: "Reason",
            extraClasses: "w-180 hidden lg:block truncate",
            sortKey: "",
            isClickable: false,
          },
        ]
      : headers;

  // Map sort icons to display headers (matching original header indices)
  const getSortIconForDisplayHeader = (displayIndex: number) => {
    if (tableFilter === RequestState.Cancelled) {
      // Find the original index for this display header
      const displayHeader = displayHeaders[displayIndex];
      if (displayHeader.title === "Reason") {
        return null; // Reason column is not sortable
      }
      const originalIndex = headers.findIndex(h => h.title === displayHeader.title);
      return originalIndex >= 0 && activeSortIndex === originalIndex ? (sortMethods[originalIndex] ? headerIconAsc : headerIconDesc) : null;
    } else {
      return activeSortIndex === displayIndex ? (sortMethods[displayIndex] ? headerIconAsc : headerIconDesc) : null;
    }
  };

  useEffect(() => {
    handleSort(2, true);
    dispatch(setActiveIndex(0));

    const fetchPCP = async () => {
      try {
        const res = await dispatch(getAllPCP({ officeId: id }));
        if (res.payload) {
          setPcpData(res.payload as Pcp[]);
        }
      } catch (error) {
        // console.error(error);
      }
    };
    fetchPCP();
  }, [dispatch, id]);

  useEffect(() => {
    if (requestCounts !== null) {
      const newCounts = [...statusCounts];
      newCounts.forEach(count => {
        count.count = (requestCounts as { [k: string]: number })[count.id];
      });
      setStatusCounts(newCounts);
    }
  }, [requestCounts]);

  useEffect(() => {
    if (id) {
      const officeId = id;
      if (id !== currentOffice?.officeId && !loadingOffice) dispatch(getOffice({ officeId }));
      dispatch(
        getOfficeUsersWithPermission({
          officeId,
          permission: AuthPermission.Request,
          page: 0,
          sort: "",
          size: 100,
        })
      );
      dispatch(
        searchServiceData({
          officeId: officeId,
          page: 0,
          size: 1,
          sort: "name,asc",
          enabledOnly: true,
          serviceOwner: ServiceOwner.Office,
        })
      );
    }
  }, [id, dispatch]);

  useEffect(() => {
    const isAlertShown = sessionStorage.getItem(SESSION_STORAGE_TEMP_CLOSED);
    if (currentOffice?.tempClosed && currentOffice.officeId && isAlertShown !== currentOffice.officeId) {
      setShowCloseAlert(true);
      sessionStorage.setItem(SESSION_STORAGE_TEMP_CLOSED, currentOffice.officeId);
    }
  }, [currentOffice]);

  return (
    <main>
      <div className="container mx-auto p-8 pb-4">
        <OfficeSectionsHeader showMeta={true} />

        {/* Title and Create Button Row */}
        <div className="flex justify-between items-center mb-4">
          <h2 className="heading uppercase">Requests</h2>
          <ProtectedComponent requiredPermission={[AuthPermission.Request]}>
            <Button
              text="Request a Service"
              onClick={() => {
                currentOffice?.officeState !== OfficeState.Published
                  ? toast?.errorToast("Office must be published before creating a request")
                  : !serviceList?.data?.content?.length
                  ? toast?.errorToast("Add services to your office in order to create a request")
                  : !currentOffice?.tempClosed
                  ? navigate(`create-request`)
                  : setShowCloseAlert(true);
              }}
              className={`whitespace-nowrap ${
                !serviceList?.data?.content?.length || currentOffice?.officeState !== OfficeState.Published ? "opacity-30" : ""
              }`}
            />
          </ProtectedComponent>
        </div>

        {/* Filters Row */}
        <div className="flex flex-wrap items-center gap-3 mb-6">
          <select
            className="general-select min-w-[140px] max-w-[220px] pr-8 truncate"
            value={selectedPcp}
            onChange={e => {
              handleSelectPCP(e.currentTarget.value);
            }}
          >
            <option value="">All PCPs</option>
            {pcpData?.map((user: any) => {
              return <option value={user.userId} key={user.userId}>{`${user.firstName} ${user.lastName}`}</option>;
            })}
          </select>

          <select
            className="general-select min-w-[140px] max-w-[220px] pr-8 truncate"
            value={selectedUser}
            onChange={e => {
              handleAllUserSelect(e.currentTarget.value);
            }}
          >
            <option value="">All Users</option>
            {userWithPermission
              ? userWithPermission.data?.content
                  .filter(user => user.userState === "active")
                  ?.map((user: User) => {
                    return <option value={user.userId} key={user.userId}>{`${user.firstName} ${user.lastName}`}</option>;
                  })
              : null}
          </select>

          <select
            className="general-select min-w-[150px] max-w-[200px] pr-8 truncate"
            value={selectedArchiveState}
            onChange={e => {
              const val: ArchiveState = e.currentTarget.value as ArchiveState;
              handleArchiveStateChange(val);
            }}
          >
            <option value={`${ArchiveState.Any}`}>All Requests</option>
            <option value={`${ArchiveState.NotArchived}`}>Active Requests</option>
            <option value={`${ArchiveState.Archived}`}>Archived Requests</option>
          </select>

          <div className="min-w-[200px] max-w-[280px]">
            <TextInput value={term} onChangeFunc={handleSearch} placeholder={"Search Requests"} extraInputClass="w-full" isSearchInput={true} />
          </div>
        </div>
        <div className="flex mb-6 w-full overflow-auto">
          {statusCounts.map(count => {
            return (
              <StatusCount
                onClick={() => {
                  selectedArchiveState !== ArchiveState.Archived ? onStatusCountClick(count.id) : null;
                }}
                title={count.title}
                count={selectedArchiveState !== ArchiveState.Archived ? count.count : "-"}
                key={count.id}
                active={tableFilter === count.type}
                countColor={count.isDanger ? "danger" : "primary"}
              />
            );
          })}
        </div>
        {!loading ? (
          officeRequestList?.data?.content ? (
            <div className="block">
              {officeRequestList?.data?.content && officeRequestList?.data?.content?.length > 0 ? (
                <>
                  <div className="flex justify-between items-center border-b border-docnote-blue-light border-opacity-50">
                    {displayHeaders.map((head, displayIndex) => {
                      // Find original header index for sorting
                      const originalIndex =
                        tableFilter === RequestState.Cancelled && head.title === "Reason"
                          ? -1 // Reason is not sortable
                          : headers.findIndex(h => h.title === head.title);

                      return (
                        <TableHeader
                          title={head.title}
                          index={originalIndex >= 0 ? originalIndex : displayIndex}
                          key={displayIndex}
                          sortIcon={getSortIconForDisplayHeader(displayIndex)}
                          handleSort={head.isClickable && originalIndex >= 0 ? handleSort : () => {}}
                          extraClassesHeader={head.extraClasses}
                          isClickable={head.isClickable}
                        />
                      );
                    })}
                    <div className="w-auto p-5 py-3">
                      <span className="text-base text-docsigna-purple-dark inline-block w-5"></span>
                    </div>
                  </div>
                  {officeRequestList.data?.content.map((rowData: Request, index: number) => {
                    // Build data object based on display headers order
                    const buildDataRow = () => {
                      const dataRow: any[] = [];
                      displayHeaders.forEach(header => {
                        switch (header.title) {
                          case "":
                            dataRow.push(rowData.urgent ? <span className="dot"></span> : <span className="w-2.5 h-2.5 block"></span>);
                            break;
                          case "Patient Name":
                            dataRow.push(
                              <RenderName
                                title={rowData.firstName + " " + rowData.lastName}
                                subTitle={`${
                                  rowData.assignedTo ? `Assigned: ${rowData.assignedTo.firstName} ${rowData.assignedTo.lastName}` : "Unassigned"
                                }`}
                                extraClasses="ml-0"
                              />
                            );
                            break;
                          case "Request #":
                            dataRow.push(rowData.requestNumber);
                            break;
                          case "Service":
                            dataRow.push(
                              <RenderName
                                extraClasses="text-sm"
                                title={rowData.service ? rowData.service.name : "--"}
                                subTitle={`PCP: ${
                                  rowData.primaryCareProvider
                                    ? `${rowData.primaryCareProvider.firstName} ${rowData.primaryCareProvider.lastName}`
                                    : "Unassigned"
                                }`}
                              />
                            );
                            break;
                          case "Bill To":
                            dataRow.push(
                              rowData?.serviceBilledToOverride
                                ? billedToOptions[rowData?.serviceBilledToOverride]
                                : rowData?.service?.serviceBilledTo
                                ? billedToOptions[rowData?.service?.serviceBilledTo]
                                : "-"
                            );
                            break;
                          case "Status":
                            dataRow.push(
                              rowData?.refundRequest?.refundRequestState === RefundRequestState.Pending ? (
                                <span className={`text-docsigna-orange`}>Refund Requested</span>
                              ) : (
                                renderState[rowData.state]
                              )
                            );
                            break;
                          case "Paid":
                            dataRow.push(rowData?.paymentComplete ? <FiberManualRecordIcon htmlColor={`${colors.Red}`} fontSize="small" /> : "");
                            break;
                          case "Updated":
                            dataRow.push(moment(rowData.updatedDate).format("ll"));
                            break;
                          case "Comments":
                            dataRow.push(
                              <>
                                {rowData.numMessages ? rowData.numMessages : 0}
                                {rowData.numUnreadMessages ? (
                                  <span className="text-xs ml-1 text-green-700 font-medium py-1 px-2 bg-green-100 rounded-full">
                                    {rowData.numUnreadMessages} New
                                  </span>
                                ) : null}
                              </>
                            );
                            break;
                          case "Reason":
                            const reasonText = rowData.cancellationReason || "-";
                            dataRow.push(
                              reasonText !== "-" ? (
                                <MuiTooltip
                                  title={reasonText}
                                  arrow
                                  placement="top"
                                  componentsProps={{
                                    tooltip: {
                                      sx: {
                                        fontSize: "14px",
                                        maxWidth: "400px",
                                      },
                                    },
                                  }}
                                >
                                  <span className="truncate block cursor-default">{reasonText}</span>
                                </MuiTooltip>
                              ) : (
                                reasonText
                              )
                            );
                            break;
                          default:
                            dataRow.push("");
                        }
                      });
                      return dataRow;
                    };

                    const sortedData = buildDataRow();
                    return (
                      <TableRow
                        tooltipVisible={tooltipStates[index]}
                        handleOpenTooltip={() => handleToolTipOpen(index)}
                        handleCloseTooltip={() => handleToolTipClose()}
                        data={sortedData}
                        key={index}
                        extraClassesRow={displayHeaders.map(header => header.extraClasses)}
                        tooltipOptions={moreOptions}
                        handleTooltipClick={type => handleTooltipClick(type, rowData.requestId)}
                        onRowClick={() => {
                          onRowClick(rowData.requestId);
                        }}
                      />
                    );
                  })}
                  <Pagination
                    colSpan={10}
                    count={officeRequestList.data ? officeRequestList.data.totalElements : 0}
                    onPageChange={onPageChange}
                    onRowsPerPageChange={onRowsPerPageChange}
                    page={sort.pageNumber}
                    rowsPerPage={sort.rowsPerPage}
                  />
                </>
              ) : (
                <DataNotFound image={NoRequestFound} text="No requests found" />
              )}
            </div>
          ) : null
        ) : (
          <CircularProgress size={40} color={"info"} style={{ marginLeft: "50%", marginTop: "10%" }} />
        )}
      </div>
      <ConfirmationDialog
        open={showCloseAlert}
        description={renderCloseMessage()}
        handleFailure={() => setShowCloseAlert(false)}
        handleSuccess={async () => {
          if (currentOffice?.officeId) {
            setIsLoading(true);
            const res = await OfficeService.setOfficeTemporarilyClosed(currentOffice?.officeId, false);
            if (res) {
              await dispatch(getOffice({ officeId: currentOffice?.officeId }));
              navigate(`create-request`);
            }
            setIsLoading(false);
          }
        }}
        canClickOutSide={false}
        loading={isLoading}
        successButtonText="Open Office"
        failureButtonText="Remain Closed"
        //hideActionButtons={false}
      />
    </main>
  );
};

export default OfficeDetailsScreen;
